<template>
    <Modal
        title="Client Details"
        :model-value="isModalVisible"
        data-testid="client-details__modal"
        @onCancel="closeClientModal"
    >
        <hox-loading-layer v-if="isLoadingCreativeX || isLoadingCreativeXSecrets" :is-full-screen="false" />
        <Form
            ref="form"
            class="edit-client"
            :show-message="false"
            :model="form"
            :label-width="120"
            :rules="validationRules"
            @submit.prevent
        >
            <FormItem label="Client name" prop="name">
                <Input
                    ref="name"
                    v-model="form.name"
                    auto-complete="off"
                    data-testid="edit-client__name"
                    @keydown.enter="confirmAddClient"
                />
            </FormItem>

            <template v-if="userIsSuperAdmin && form.creativeXPlatformSettings?.enabled">
                <FormItem label="CreativeX Secret" prop="creativeXPlatformSettings.secretId">
                    <Select
                        v-model="form.creativeXPlatformSettings.secretId"
                        data-testid="edit-client__secret-id"
                        @keydown.enter="confirmAddClient"
                    >
                        <Option v-for="option in creativeXSecrets" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </Option>
                    </Select>
                    <CreativeXError v-if="creativeXSecretsHasError" @refetch="fetchCreativeXSecrets" />
                </FormItem>

                <FormItem
                    v-if="form.creativeXPlatformSettings.secretId"
                    label="Brand ID"
                    prop="creativeXPlatformSettings.brandId"
                >
                    <Select
                        v-model="form.creativeXPlatformSettings.brandId"
                        data-testid="edit-client__brand-id"
                        @keydown.enter="confirmAddClient"
                    >
                        <Option v-for="option in creativeXOptions?.brands" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </Option>
                    </Select>
                    <CreativeXError v-if="creativeXHasError" @refetch="fetchCreativeXDimensions" />
                </FormItem>
            </template>

            <template v-if="isEditClient">
                <FormItem v-if="userIsSuperAdmin" label="Facebook">
                    <Input
                        v-model="form.fbBusinessId"
                        placeholder="Business ID"
                        auto-complete="off"
                        data-testid="edit-client__fb-business-id"
                        @keydown.enter="confirmAddClient"
                    />
                    <Input
                        v-model="form.fbAdAccountId"
                        placeholder="Ad Account ID"
                        auto-complete="off"
                        data-testid="edit-client__fb-account-id"
                        @keydown.enter="confirmAddClient"
                    />
                </FormItem>

                <FormItem label="Logo">
                    <ImageUpload
                        :model-value="form.logo"
                        :config="form.uploadConfig as MastheadUploadConfig"
                        data-testid="edit-client__logo"
                        @imageUploading="disableConfirm = true"
                        @imageUploaded="onImageUploaded"
                        @imageUploadFailed="disableConfirm = false"
                    />
                </FormItem>

                <FormItem v-if="$auth.userCan($auth.Actions.CanEnableImageProcessing)">
                    <Checkbox v-model="form.imageProcessingEnabled" data-testid="edit-client__enable-image-processing">
                        Enable Generative AI features
                    </Checkbox>
                </FormItem>

                <FormItem v-if="$auth.userCan($auth.Actions.CanEnableConnectModule)">
                    <Checkbox v-model="form.connectModuleEnabled" data-testid="edit-client__enable-connect-module">
                        Enable Connect Module
                    </Checkbox>
                </FormItem>

                <FormItem v-if="userIsSuperAdmin && form.creativeXPlatformSettings">
                    <Checkbox
                        v-model="form.creativeXPlatformSettings.enabled"
                        :disabled="form.sataliaPlatformSettings?.enabled"
                        data-testid="edit-client__enable-creative-x"
                    >
                        Enable CreativeX
                    </Checkbox>
                    <p v-if="form.sataliaPlatformSettings?.enabled" class="warning-text">
                        Disable Pre-flight first to enable CreativeX
                    </p>
                </FormItem>

                <FormItem v-if="userIsSuperAdmin && form.sataliaPlatformSettings">
                    <Checkbox
                        v-model="form.sataliaPlatformSettings.enabled"
                        :disabled="form.creativeXPlatformSettings?.enabled"
                        data-testid="edit-client__enable-satalia"
                    >
                        Enable Pre-flight
                    </Checkbox>
                    <p v-if="form.creativeXPlatformSettings?.enabled" class="warning-text">
                        Disable CreativeX first to enable Pre-flight
                    </p>
                </FormItem>

                <FormItem
                    v-if="form.sataliaPlatformSettings?.enabled"
                    label="Pre-flight Domain"
                    prop="sataliaPlatformSettings.domain"
                >
                    <hox-loading-spinner v-if="isLoadingSatalia" />
                    <Select
                        v-else
                        v-model="form.sataliaPlatformSettings.domain"
                        placeholder="Select Pre-flight domain"
                        data-testid="edit-client__satalia-domain"
                        :disabled="sataliaDomains.length === 0"
                    >
                        <Option v-for="domain in sataliaDomains" :key="domain" :value="domain">
                            {{ domain }}
                        </Option>
                    </Select>
                    <p v-if="sataliaHasError" class="error-text">Failed to load domains. Please try again.</p>
                </FormItem>

                <FormItem
                    v-if="form.creativeXPlatformSettings?.enabled"
                    label="Passing threshold"
                    prop="creativeXPlatformSettings.threshold"
                >
                    <InputNumber
                        v-model="form.creativeXPlatformSettings.threshold"
                        class="edit-client__threshold"
                        :min="1"
                        :max="99"
                        placeholder="Enter a value between 1 and 99"
                        data-testid="edit-client__threshold"
                        :parser="(value: string) => value.replace('.', '')"
                    />
                </FormItem>
            </template>
        </Form>

        <template #footer>
            <span class="dialog-footer">
                <Button
                    v-if="isEditClient && $auth.userCan($auth.Actions.CanRemoveClient, { clientId: form.id })"
                    class="edit-client-button__delete"
                    icon="ios-trash-outline"
                    data-testid="edit-client-button__delete"
                    type="error"
                    ghost
                    @click="deleteClient(form)"
                >
                    Delete Client
                </Button>
                <Button
                    class="edit-client-button__cancel"
                    data-testid="edit-client-button__cancel"
                    @click="closeClientModal"
                >
                    Cancel
                </Button>
                <Button
                    class="edit-client-button__confirm"
                    data-testid="edit-client-button__confirm"
                    type="primary"
                    :disabled="disableConfirm || isLoading"
                    @click="confirmAddClient"
                >
                    Confirm
                </Button>
            </span>
        </template>
    </Modal>
</template>

<script lang="ts">
import bus from "@/bus";

import ImageUpload from "@/components/ImageUpload.vue";
import CreativeXError from "@/components/CreativeXError.vue";

import addClientMutation from "@/apollo/mutations/AddClient.gql";
import removeClientMutation from "@/apollo/mutations/RemoveClient.gql";
import updateClientMutation from "@/apollo/mutations/UpdateClient.gql";
import clientsQuery from "@/apollo/queries/Clients.gql";
import { AuthGetters } from "@/store/modules/auth";
import type { Client, Err, IViewForm, MastheadUploadConfig } from "@/types/types";
import type { PropType } from "vue";
import { getCreativeXDimensions, getCreativeXSecrets } from "@/utils/CreativeIntelligenceApi/creativex";
import { getSataliaDomains } from "@/utils/CreativeIntelligenceApi/satalia";
import formatCreativeXDimensions from "@/utils/CreativeX/formatCreativeXDimensions";
import { CreativeXScore } from "hoxton-shared/enums/creativex";
import type { CreativeXSecret } from "@/types/preflight/creativeX";

export type ClientFormData = {
    _id: string;
    id: string;
    name: string;
    logo: string;
    content?: string;
    connectModuleEnabled?: boolean;
    creativeXPlatformSettings?: {
        enabled: boolean;
        brandId: string | null;
        threshold: number | null;
        secretId: string | null;
    } | null;
    sataliaPlatformSettings?: {
        enabled: boolean;
        domain: string | null;
    } | null;
    imageProcessingEnabled?: boolean;
    fbAdAccountId?: string;
    fbBusinessId?: string;
    uploadConfig?: MastheadUploadConfig;
};

export default {
    name: "ClientModal",
    components: {
        ImageUpload,
        CreativeXError
    },
    props: {
        isModalVisible: {
            type: Boolean,
            required: true
        },
        client: {
            type: [Object, null] as PropType<Client | null>,
            required: true
        },
        sortClientsBy: {
            type: String,
            required: true
        }
    },

    emits: ["close"],

    data() {
        return {
            form: this.resetForm() as ClientFormData,
            validationRules: {
                "creativeXPlatformSettings.brandId": [
                    { required: true, message: "Please select a brand", trigger: "change" }
                ],
                "creativeXPlatformSettings.threshold": [
                    {
                        type: "number",
                        required: true,
                        min: 0,
                        max: 100,
                        message: "Threshold must be between 0 and 100",
                        trigger: "blur"
                    }
                ],
                "creativeXPlatformSettings.secretId": [
                    { required: true, message: "Please select a secret", trigger: "change" }
                ],
                "sataliaPlatformSettings.domain": [
                    { required: true, message: "Please select a domain", trigger: "change" }
                ],
                name: [{ required: true, message: "Client name is a required field", whitespace: true }]
            },
            isLoading: false,
            isLoadingCreativeX: false,
            isLoadingCreativeXSecrets: false,
            disableConfirm: false,
            isPopulatingForm: false,
            creativeXOptions: null as ReturnType<typeof formatCreativeXDimensions>,
            creativeXSecrets: [] as CreativeXSecret[],
            creativeXHasError: false,
            creativeXSecretsHasError: false,
            sataliaDomains: [] as string[],
            isLoadingSatalia: false,
            sataliaHasError: false
        };
    },

    computed: {
        isEditClient() {
            return this.form.id;
        },

        userIsSuperAdmin() {
            return this.$store.getters[AuthGetters.isSuperAdmin];
        }
    },

    watch: {
        isModalVisible: {
            handler(newVal: boolean) {
                if (!newVal) {
                    return;
                }

                this.populateForm();

                if (this.form.creativeXPlatformSettings?.enabled) {
                    this.fetchCreativeXSecrets();

                    // Only fetch dimensions if a secret is selected
                    if (this.form.creativeXPlatformSettings.secretId) {
                        this.fetchCreativeXDimensions();
                    }
                }

                if (this.form.sataliaPlatformSettings?.enabled) {
                    this.fetchSataliaDomains();
                }
            },
            immediate: true
        },
        "form.creativeXPlatformSettings.enabled"(newVal: boolean) {
            if (!newVal) {
                return;
            }

            this.fetchCreativeXSecrets();
        },
        "form.creativeXPlatformSettings.secretId"(newVal: string | null) {
            // Don't clear brandId when populating form with existing data
            if (!this.isPopulatingForm) {
                if (this.form.creativeXPlatformSettings) {
                    this.form.creativeXPlatformSettings.brandId = null;
                }

                this.creativeXOptions = null;
            }

            if (!newVal) {
                return;
            }

            this.fetchCreativeXDimensions();
        },
        "form.sataliaPlatformSettings.enabled"(newVal: boolean) {
            if (!newVal) {
                return;
            }

            this.fetchSataliaDomains();
        },
        client: {
            handler() {
                this.populateForm();
            },
            immediate: true
        }
    },

    methods: {
        populateForm() {
            if (this.client) {
                this.isPopulatingForm = true;
                this.form._id = this.client._id;
                this.form.id = this.client.id;
                this.form.name = this.client.name;
                this.form.fbBusinessId = this.client.fbAdsManagerConfig?.businessId;
                this.form.fbAdAccountId = this.client.fbAdsManagerConfig?.adAccountId;
                this.form.uploadConfig = this.client.mastheadUploadConfig;
                this.form.logo = this.client.logoUrl ?? "";
                this.form.content = "";
                this.form.imageProcessingEnabled = this.client.imageProcessingEnabled;
                this.form.connectModuleEnabled = this.client.connectModuleEnabled;
                this.form.creativeXPlatformSettings = {
                    brandId: this.client.creativeXPlatformSettings?.brandId?.toString() || null,
                    enabled: this.client.creativeXPlatformSettings?.enabled || false,
                    threshold: Math.round(
                        (this.client.creativeXPlatformSettings?.threshold ?? CreativeXScore.THRESHOLD) * 100
                    ),
                    secretId: this.client.creativeXPlatformSettings?.secretId || null
                };
                this.form.sataliaPlatformSettings = {
                    domain: this.client.sataliaPlatformSettings?.domain || null,
                    enabled: this.client.sataliaPlatformSettings?.enabled || false
                };

                this.$nextTick(() => {
                    this.isPopulatingForm = false;
                });
            }
        },
        closeClientModal() {
            this.form = this.resetForm();
            this.$emit("close");
        },

        confirmAddClient() {
            (this.$refs.form as IViewForm).validate((valid: boolean) => {
                if (!valid) {
                    this.$snackbar.error("Please fill in the required fields");

                    return;
                }

                if (!this.isEditClient) {
                    this.addClient();
                } else {
                    if (
                        (this.form.fbAdAccountId && !this.form.fbBusinessId) ||
                        (!this.form.fbAdAccountId && this.form.fbBusinessId)
                    ) {
                        this.$snackbar.error(
                            "Please ensure both Facebook ID fields are filled out: Business ID and Ad Account ID."
                        );

                        return;
                    }
                    this.patchClient();
                }
            });
        },

        deleteClient(form: ClientFormData) {
            this.$Modal.confirm({
                title: `Delete client ${form.name}?`,
                content: `Deleting the client "${form.name}" is permanent and can not be undeleted.`,
                okText: "Delete",
                onOk: async () => {
                    this.isLoading = true;
                    bus.$emit("percentLoadEvent", 0);
                    try {
                        await this.$apollo.mutate({
                            mutation: removeClientMutation,
                            variables: {
                                clientId: form.id
                            },
                            refetchQueries: [
                                {
                                    query: clientsQuery,
                                    variables: {
                                        orderBy: this.sortClientsBy,
                                        wppProjectId: this.$store.state.auth?.wppProjectId
                                    }
                                }
                            ]
                        });

                        bus.$emit("percentLoadEvent", 100);
                        this.$snackbar.success("Client deleted");
                        this.closeClientModal();
                    } catch (err) {
                        this.$snackbar.error((err as Err).message);
                    }

                    this.isLoading = false;
                }
            });
        },

        addClient() {
            if (this.isLoading) {
                return;
            }

            bus.$emit("percentLoadEvent", 0);
            this.isLoading = true;

            this.$apollo
                .mutate({
                    mutation: addClientMutation,
                    variables: {
                        name: this.form.name
                    },
                    refetchQueries: [
                        {
                            query: clientsQuery,
                            variables: {
                                orderBy: this.sortClientsBy,
                                wppProjectId: this.$store.state.auth?.wppProjectId
                            }
                        }
                    ]
                })
                .then(() => {
                    this.$snackbar.success(`The client "${this.form.name}" has been added.`);
                    this.closeClientModal();
                })
                .catch(err => {
                    this.$snackbar.error(err.message);
                })
                .finally(() => {
                    bus.$emit("percentLoadEvent", 100);
                    this.isLoading = false;
                });
        },

        patchClient() {
            if (this.isLoading) {
                return;
            }

            this.isLoading = true;
            bus.$emit("percentLoadEvent", 0);

            const fbAdsManagerConfig = this.form.fbBusinessId &&
                this.form.fbAdAccountId && {
                    adAccountId: this.form.fbAdAccountId,
                    businessId: this.form.fbBusinessId
                };

            this.$apollo
                .mutate({
                    mutation: updateClientMutation,
                    variables: {
                        clientId: this.form._id,
                        content: this.form.content,
                        name: this.form.name,
                        imageProcessingEnabled: this.form.imageProcessingEnabled,
                        connectModuleEnabled: this.form.connectModuleEnabled,
                        creativeXPlatformSettings: {
                            enabled: this.form.creativeXPlatformSettings?.enabled,
                            brandId: this.form.creativeXPlatformSettings?.enabled
                                ? Number(this.form.creativeXPlatformSettings.brandId)
                                : null,
                            threshold: this.form.creativeXPlatformSettings?.enabled
                                ? this.form.creativeXPlatformSettings.threshold! / 100
                                : null,
                            secretId: this.form.creativeXPlatformSettings?.secretId
                        },
                        sataliaPlatformSettings: {
                            enabled: this.form.sataliaPlatformSettings?.enabled,
                            domain: this.form.sataliaPlatformSettings?.enabled
                                ? this.form.sataliaPlatformSettings.domain
                                : null
                        },
                        // if not provided it should unset facebook for this client
                        fbAdsManagerConfig: fbAdsManagerConfig || null
                    },
                    refetchQueries: [
                        {
                            query: clientsQuery,
                            variables: {
                                orderBy: this.sortClientsBy,
                                wppProjectId: this.$store.state.auth?.wppProjectId
                            }
                        }
                    ]
                })
                .then(() => {
                    this.$snackbar.success(`The client "${this.form.name}" has been updated`);
                    this.closeClientModal();
                })
                .catch(err => {
                    this.$snackbar.unauthorized(err);
                })
                .finally(() => {
                    bus.$emit("percentLoadEvent", 100);
                    this.isLoading = false;
                });
        },

        resetForm() {
            return {
                _id: "",
                id: "",
                name: "",
                uploadConfig: undefined,
                logo: "",
                content: "",
                imageProcessingEnabled: undefined,
                connectModuleEnabled: undefined,
                creativeXPlatformSettings: {
                    enabled: false,
                    brandId: null,
                    threshold: null,
                    secretId: null
                },
                sataliaPlatformSettings: {
                    enabled: false,
                    domain: null
                },
                fbBusinessId: "",
                fbAdAccountId: ""
            };
        },

        onImageUploaded(event: string) {
            this.disableConfirm = false;
            this.form.content = event;
        },

        async fetchCreativeXDimensions() {
            if (this.isLoadingCreativeX) {
                return;
            }

            const secretId = this.form.creativeXPlatformSettings?.secretId;
            if (!secretId) {
                return;
            }

            this.creativeXHasError = false;
            this.isLoadingCreativeX = true;
            try {
                const oauthConfig = this.$store.getters[AuthGetters.oauthConfig];

                if (!oauthConfig?.apiURL) {
                    throw new Error("Missing API URL");
                }

                const { data } = await getCreativeXDimensions(oauthConfig.apiURL, secretId);

                this.creativeXOptions = formatCreativeXDimensions(data);
            } catch {
                this.creativeXHasError = true;
                this.$snackbar.error("Failed to fetch CreativeX dimensions");
            } finally {
                this.isLoadingCreativeX = false;
            }
        },

        async fetchCreativeXSecrets() {
            if (this.isLoadingCreativeXSecrets) {
                return;
            }

            this.creativeXSecretsHasError = false;
            this.isLoadingCreativeXSecrets = true;

            try {
                const oauthConfig = this.$store.getters[AuthGetters.oauthConfig];

                if (!oauthConfig?.apiURL) {
                    throw new Error("Missing API URL");
                }

                const { data } = await getCreativeXSecrets(oauthConfig.apiURL);

                this.creativeXSecrets = data;
            } catch {
                this.creativeXSecretsHasError = true;
                this.$snackbar.error("Failed to fetch CreativeX secrets");
            } finally {
                this.isLoadingCreativeXSecrets = false;
            }
        },

        async fetchSataliaDomains() {
            if (this.isLoadingSatalia) {
                return;
            }

            this.sataliaHasError = false;
            this.isLoadingSatalia = true;
            try {
                const oauthConfig = this.$store.getters[AuthGetters.oauthConfig];

                if (!oauthConfig?.apiURL) {
                    throw new Error("Missing API URL");
                }

                const { data } = await getSataliaDomains(oauthConfig.apiURL);

                this.sataliaDomains = data || [];
            } catch {
                this.sataliaHasError = true;
            } finally {
                this.isLoadingSatalia = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

.edit-client-button__delete {
    float: left;
}

.edit-client__threshold {
    width: 100%;
}

.warning-text {
    color: var(--wpp-warning-color-500);
    font-size: 12px;
    margin-top: 4px;
    margin-bottom: 0;
}

.error-text {
    color: var(--wpp-danger-color-500);
    font-size: 12px;
    margin-top: 4px;
    margin-bottom: 0;
}
</style>
